magna.athletesCarousel = parent => {
  const swiperContainer = parent.querySelector('.slideshow--athletes')
  if (!swiperContainer) return

  const swiper = new Swiper(swiperContainer, {
    slidesPerView: 1,
    loop: false,
    autoplay: { delay: 3000, disableOnInteraction: false },
    navigation: {
      nextEl: parent.querySelector('.button--next'),
      prevEl: parent.querySelector('.button--prev'),
    },
    pagination: {
      el: parent.querySelector('.slideshow__dots-container'),
      clickable: true,
      bulletClass: 'slideshow__dot',
      bulletActiveClass: 'slideshow__dot--selected',
    },
  })

  return swiper
}

magna.alternatingSlider = parent => {
  const swiperContainer = parent.querySelector('.alternating-slider__slider-container')
  if (!swiperContainer) return

  const swiper = new Swiper(swiperContainer, {
    slidesPerView: 1,
    loop: false,
    allowTouchMove: true,
  })

  return swiper
}

magna.equalizeAnnouncementSlideHeights = parent => {
  if (!parent) return

  const slides = parent.querySelectorAll(
    '.announcement-bar__container.swiper-slide, .ctr-custom-announcement__slide.swiper-slide',
  )
  if (slides.length < 2) return

  let maxHeight = 0

  slides.forEach(slide => {
    slide.style.minHeight = ''
  })

  slides.forEach(slide => {
    maxHeight = Math.max(maxHeight, slide.offsetHeight)
  })

  if (maxHeight <= 0) return

  slides.forEach(slide => {
    slide.style.minHeight = `${maxHeight}px`
  })
}

magna.selectAnnouncementSlide = (parent, { blockId, blockEl } = {}) => {
  if (!parent) return

  const swiper = parent._announcementSwiper
  if (!swiper) return

  if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
    swiper.autoplay.stop()
  }

  let slide =
    (blockEl && blockEl.classList && blockEl.classList.contains('swiper-slide') && blockEl) ||
    blockEl?.closest?.('.swiper-slide') ||
    null

  if (!slide && blockId) {
    const candidates = parent.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
    slide =
      [...candidates].find(node => {
        if (node.dataset.blockId === blockId) return true
        const editorBlock = node.getAttribute('data-shopify-editor-block')
        return editorBlock && editorBlock.indexOf(blockId) !== -1
      }) || null
  }

  if (!slide) return

  const loopIndexAttr = slide.getAttribute('data-swiper-slide-index')
  const loopIndex =
    loopIndexAttr != null
      ? parseInt(loopIndexAttr, 10)
      : [...parent.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')].indexOf(slide)

  if (Number.isNaN(loopIndex) || loopIndex < 0) return

  if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
    swiper.slideToLoop(loopIndex, 0)
  } else {
    swiper.slideTo(loopIndex, 0)
  }

  magna.equalizeAnnouncementSlideHeights(parent)
  magna.updateAnnouncementOffset?.()
}

magna.announcementBar = parent => {
  const swiperContainer =
    parent.querySelector('.announcement-bar__section') ||
    parent.querySelector('.ctr-custom-announcement__section')
  if (!swiperContainer) return

  if (parent._announcementSwiper) {
    parent._announcementSwiper.destroy(true, true)
    parent._announcementSwiper = null
  }

  const isDesignMode = !!(window.Shopify && Shopify.designMode)

  const equalize = () => {
    magna.equalizeAnnouncementSlideHeights(parent)
    magna.updateAnnouncementOffset?.()
  }

  const swiper = new Swiper(swiperContainer, {
    slidesPerView: 1,
    loop: !isDesignMode,
    autoHeight: false,
    // Keep slides still while editing blocks in the theme editor
    autoplay: isDesignMode ? false : { delay: 5000, disableOnInteraction: true },
    allowTouchMove: !isDesignMode,
    navigation: {
      nextEl: parent.querySelector('.button--next'),
      prevEl: parent.querySelector('.button--prev'),
    },
    on: {
      init: equalize,
      resize: equalize,
      breakpoint: equalize,
    },
  })

  parent._announcementSwiper = swiper

  swiper.on('slideChangeTransitionEnd', () => {
    magna.updateAnnouncementOffset?.()
  })

  // Loop mode duplicates slides after init — equalize again once they’re in the DOM.
  requestAnimationFrame(equalize)
  setTimeout(equalize, 50)

  magna.updateAnnouncementOffset?.()

  return swiper
}

const getVisibleAnnouncementBar = () => {
  const candidates = [
    ...document.querySelectorAll('.ctr-custom-announcement'),
    ...document.querySelectorAll('.announcement-bar'),
  ]

  return (
    candidates.find(node => {
      if (!node || !node.isConnected) return false

      const styles = window.getComputedStyle(node)
      if (styles.display === 'none' || styles.visibility === 'hidden') return false

      const section = node.closest('.shopify-section') || node
      if (section) {
        const sectionStyles = window.getComputedStyle(section)
        if (sectionStyles.display === 'none' || sectionStyles.visibility === 'hidden') return false
      }

      return node.getBoundingClientRect().height > 0 || styles.position === 'fixed'
    }) || null
  )
}

const isAnnouncementStickyEnabled = bar => {
  if (!bar) return false
  const value = bar.getAttribute('data-announcement-sticky')
  if (value != null) return value !== 'false'
  const styles = window.getComputedStyle(bar)
  return styles.position === 'sticky' || styles.position === 'fixed'
}

magna.updateAnnouncementOffset = () => {
  const root = document.documentElement
  const bar = getVisibleAnnouncementBar()
  const gap = 0

  if (!bar) {
    root.style.setProperty('--announcement-total-height', '0px')
    root.style.setProperty('--announcement-bar-top', '0px')
    return
  }

  const styles = window.getComputedStyle(bar)
  const barTop = parseFloat(styles.top) || 0
  const barHeight = bar.offsetHeight
  const baseHeight = barTop + barHeight + gap
  const stickyEnabled = isAnnouncementStickyEnabled(bar)

  if (stickyEnabled) {
    bar.style.transform = ''
    root.style.setProperty('--announcement-bar-top', `${barTop}px`)
    root.style.setProperty('--announcement-total-height', `${baseHeight}px`)
    return
  }

  // Sticky disabled: keep initial visual (bar above header), scroll bar away,
  // and let only the header stick to the top.
  if (styles.position === 'fixed') {
    const scrollY = window.scrollY || window.pageYOffset || 0
    const shift = Math.min(scrollY, baseHeight)
    bar.style.transform = shift > 0 ? `translateY(${-shift}px)` : ''
    root.style.setProperty('--announcement-bar-top', `${barTop}px`)
    root.style.setProperty('--announcement-total-height', `${Math.max(0, baseHeight - scrollY)}px`)
    return
  }

  // In-flow bar (announcement_bar): header sticks at 0, bar scrolls away naturally
  bar.style.transform = ''
  root.style.setProperty('--announcement-bar-top', `${barTop}px`)
  root.style.setProperty('--announcement-total-height', '0px')
}

magna._announcementOffsetObserver = null

magna.observeAnnouncementBars = () => {
  if (typeof ResizeObserver === 'undefined') return

  if (!magna._announcementOffsetObserver) {
    magna._announcementOffsetObserver = new ResizeObserver(() => {
      magna.updateAnnouncementOffset()
    })
  }

  document.querySelectorAll('.ctr-custom-announcement, .announcement-bar').forEach(node => {
    magna._announcementOffsetObserver.observe(node)
  })
}

magna.scheduleAnnouncementOffsetUpdate = () => {
  requestAnimationFrame(() => {
    magna.updateAnnouncementOffset()
    // Theme editor sometimes needs a second pass after section re-render settles.
    setTimeout(() => magna.updateAnnouncementOffset(), 50)
  })
}

magna.refreshAnnouncementOffset = () => {
  document.querySelectorAll('.ctr-custom-announcement, .announcement-bar').forEach(node => {
    magna.equalizeAnnouncementSlideHeights(node)
  })
  magna.observeAnnouncementBars()
  magna.scheduleAnnouncementOffsetUpdate()
}

magna.initAnnouncementOffset = () => {
  if (magna._announcementOffsetInitialized) {
    magna.refreshAnnouncementOffset()
    return
  }

  magna._announcementOffsetInitialized = true

  const update = () => magna.scheduleAnnouncementOffsetUpdate()

  update()
  magna.observeAnnouncementBars()

  window.addEventListener('resize', update)
  window.addEventListener('load', update)
  window.addEventListener('scroll', () => magna.updateAnnouncementOffset(), { passive: true })

  // Theme editor: same pattern as ctr-megamenu-shop.js
  if (window.Shopify && Shopify.designMode) {
    ;['shopify:section:load', 'shopify:section:unload', 'shopify:section:select', 'shopify:section:reorder', 'shopify:block:select', 'shopify:block:deselect'].forEach(eventName => {
      document.addEventListener(eventName, () => {
        magna.refreshAnnouncementOffset()
      })
    })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', magna.initAnnouncementOffset)
} else {
  magna.initAnnouncementOffset()
}

magna.productCarousel = {}

magna.buyCarousel = (item) => {
  const dotsEl = item.querySelector('.buy__dots')
  const prevBtn = item.querySelector('.button--prev')
  const nextBtn = item.querySelector('.button--next')

  const swiper = new Swiper(item, {
    slidesPerView: 1,
    loop: true,
    spaceBetween: 10,
    navigation: {
      nextEl: nextBtn,
      prevEl: prevBtn,
    },
    pagination: {
      el: dotsEl,
      clickable: true,
      bulletClass: 'buy__dot',
      bulletActiveClass: 'buy__dot--selected',
    },
  })

  swiper.scrollTo = swiper.slideTo.bind(swiper)
  magna.productCarousel = swiper
}

magna.drawerRecommendedCarousel = () => {
  return;
}

magna.reviewsHeroCarousel = parent => {
  const swiperContainer = parent.querySelector('.reviews-hero__slider-container')
  if (!swiperContainer) return

  const prevBtn = parent.querySelector('.reviews-hero__nav-button--prev')
  const nextBtn = parent.querySelector('.reviews-hero__nav-button--next')
  const dotsEl = parent.querySelector('.reviews-hero__dots')

  const swiper = new Swiper(swiperContainer, {
    slidesPerView: 1,
    loop: false,
    slidesPerGroup: 1,
    navigation: {
      nextEl: nextBtn,
      prevEl: prevBtn,
      disabledClass: 'reviews-hero__nav-button--disabled',
    },
    pagination: dotsEl ? {
      el: dotsEl,
      clickable: true,
      bulletClass: 'reviews-hero__dot',
      bulletActiveClass: 'reviews-hero__dot--selected',
    } : false,
  })

  return swiper
}
